import React from 'react';
import { ActionIcon, Group, Tooltip } from '@mantine/core';
import { IconThumbDown, IconThumbUp, IconFileDescription } from '@tabler/icons';
import type { DiscoverySessionMessage } from '../../../api/discoveryAgent';
import type { UpdateDiscoveryMessageFeedbackVariables } from '../../../hooks/useDiscoveryAgent';

/** Matches assistant horizontal padding in ExploreChatMessageList */
const ASSISTANT_BUBBLE_PADDING_X = 16;

export interface MessageFeedbackProps {
  messageId: string;
  currentFeedback?: DiscoverySessionMessage['feedback'];
  mutate: (vars: UpdateDiscoveryMessageFeedbackVariables) => void;
  isMutatingThisMessage: boolean;
  /** True when a one-pager has already been generated for this message */
  hasOnePager?: boolean;
  onCreateOnePager?: () => void;
  onViewOnePager?: () => void;
  isCreatingOnePager?: boolean;
}

const MessageFeedback: React.FC<MessageFeedbackProps> = ({
  messageId,
  currentFeedback,
  mutate,
  isMutatingThisMessage,
  hasOnePager,
  onViewOnePager,
}) => {
  const rating = currentFeedback?.rating;
  const iconSize = 18;
  const canPersist = Boolean(messageId);

  const handleHelpful = () => {
    if (!canPersist) return;
    if (rating === 'helpful') {
      mutate({ messageId, rating: null });
    } else {
      mutate({ messageId, rating: 'helpful' });
    }
  };

  const handleNotHelpful = () => {
    if (!canPersist) return;
    if (rating === 'not_helpful') {
      mutate({ messageId, rating: null });
    } else {
      mutate({ messageId, rating: 'not_helpful' });
    }
  };

  return (
    <Group
      spacing={4}
      mt={6}
      ml={ASSISTANT_BUBBLE_PADDING_X}
      sx={{ alignSelf: 'flex-start' }}
      id={canPersist ? `explore-msg-feedback-${messageId}` : undefined}
    >
      <Tooltip
        label={canPersist ? 'This is helpful' : 'Feedback is available after the message is saved'}
        withArrow
        withinPortal
      >
        <ActionIcon
          type="button"
          variant="subtle"
          size="sm"
          radius="md"
          aria-pressed={rating === 'helpful'}
          aria-label="This is helpful"
          disabled={!canPersist || isMutatingThisMessage}
          onClick={handleHelpful}
          sx={{
            color: rating === 'helpful' ? '#228be6' : '#868e96',
            transition: 'color 0.2s ease, transform 0.15s ease',
            '&:hover': {
              color: rating === 'helpful' ? '#1c7ed6' : '#495057',
            },
            '& svg': {
              fill: rating === 'helpful' ? 'currentColor' : 'none',
            },
          }}
        >
          <IconThumbUp size={iconSize} stroke={rating === 'helpful' ? 2 : 1.5} />
        </ActionIcon>
      </Tooltip>
      <Tooltip
        label={canPersist ? "This isn't helpful" : 'Feedback is available after the message is saved'}
        withArrow
        withinPortal
      >
        <ActionIcon
          type="button"
          variant="subtle"
          size="sm"
          radius="md"
          aria-pressed={rating === 'not_helpful'}
          aria-label="This isn't helpful"
          disabled={!canPersist || isMutatingThisMessage}
          onClick={handleNotHelpful}
          sx={{
            color: rating === 'not_helpful' ? '#e03131' : '#868e96',
            transition: 'color 0.2s ease, transform 0.15s ease',
            '&:hover': {
              color: rating === 'not_helpful' ? '#c92a2a' : '#495057',
            },
            '& svg': {
              fill: rating === 'not_helpful' ? 'currentColor' : 'none',
            },
          }}
        >
          <IconThumbDown size={iconSize} stroke={rating === 'not_helpful' ? 2 : 1.5} />
        </ActionIcon>
      </Tooltip>

      {/* One-pager: View if already created, Create otherwise */}
      {hasOnePager && (
        <Tooltip label="View one-pager" withArrow withinPortal>
          <ActionIcon
            type="button"
            variant="subtle"
            size="sm"
            radius="md"
            aria-label="View one-pager"
            onClick={onViewOnePager}
            sx={{
              color: '#868e96',
              '&:hover': { color: '#4545c8' },
            }}
          >
            <IconFileDescription size={iconSize} stroke={1.5} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
};

export default MessageFeedback;
